import { useHover } from '@react-aria/interactions'
import { mergeProps, useObjectRef } from '@react-aria/utils'
import cx from 'classnames'
import { ConditionalWrapper } from 'common/ConditionalWrapper/ConditionalWrapper'
import { KeyboardShortcutTagProps } from 'common/KeyboardShortcutTag/KeyboardShortcutTag'
import { ShortcutAwareTooltip } from 'common/Prose/utils/ShortcutAwareTooltip'
import { Button } from 'components/Button'
import { ButtonProps } from 'components/Button/Types'
import { PropsWithChildren, forwardRef } from 'react'
import Styles from './InlineFormatButton.module.scss'

export interface InlineFormatButtonProps extends ButtonProps {
  label?: string
  shortcutKeys?: KeyboardShortcutTagProps['keys']
  shortcutModifiers?: KeyboardShortcutTagProps['modifiers']
  active?: boolean
  onHoverStart?: () => void
  onHoverEnd?: () => void
}

export const InlineFormatButton = forwardRef<HTMLButtonElement, PropsWithChildren<InlineFormatButtonProps>>(
  ({ label, shortcutKeys, shortcutModifiers, children, active = false, className, ...props }, fRef) => {
    const { isDisabled, onHoverStart, onHoverEnd, size = 'lg' } = props
    const ref = useObjectRef<HTMLButtonElement>(fRef)
    const { hoverProps } = useHover({
      isDisabled,
      onHoverStart,
      onHoverEnd,
    })

    const forceActive = active
    const ariaLabel = props['aria-label'] || label

    return (
      <ConditionalWrapper
        condition={!!label}
        wrapper={(children) => (
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          <ShortcutAwareTooltip label={label!} shortcutKeys={shortcutKeys} shortcutModifiers={shortcutModifiers}>
            {children}
          </ShortcutAwareTooltip>
        )}
      >
        <Button
          ref={ref}
          {...mergeProps(props, hoverProps)}
          preventFocusOnPress
          variant="tertiary"
          aria-label={ariaLabel}
          size="lg"
          isDisabled={isDisabled}
          forceActive={forceActive}
          className={cx(Styles.button, Styles[size], className, {
            [Styles.active]: forceActive,
            [Styles.isDisabled]: isDisabled,
          })}
        >
          {children}
        </Button>
      </ConditionalWrapper>
    )
  },
)
